import { getUserAgent } from 'saddlebag-browser';
import logger from 'saddlebag-logger';

type LogWarnFields = {
  [field: string]: string | number;
  message: string;
  component: string;
};

type LogErrorFields = {
  [field: string]: unknown;
  message: string;
  component: string;
};

// Source of known bot user agents: https://github.com/Skyscanner/banana/blob/aed592d09931dadc7b458735f6920d0228a1ef35/apps/server/app/middlewares/prerender/constants.ts#L1
const KNOWN_BOT_AGENTS = [
  'http://www.google.com/adsbot.html',
  'http://www.google.com/mobile/adsbot.html',
  'googlebot/',
  'googlebot-mobile',
  'googlebot-image',
  'googlebot-news',
  'googlebot-video',
  'adsbot-google',
  'feedfetcher-google',
  'mediapartners-google',
  'mediapartners (googlebot)',
  'apis-google',
  'google-adwords-instant',
  'google web preview',
  'google-structured-data-testing-tool',
  'google favicon',
  'google-site-verification',
  'feedly/',
  'bingbot',
  'msnbot',
  'bingpreview/',
  'slurp',
  'yahoo link preview',
  'yandexbot',
  'yandeximages',
  'yandexaccessibilitybot',
  'yandexmobilebot',
  'yandexmetrika',
  'yandexturbo',
  'yandeximageresizer',
  'yandexvideo',
  'yandexadnet',
  'yandexblogs',
  'yandexcalendar',
  'yandexdirect',
  'yandexfavicons',
  'yadirectfetcher',
  'yandexfordomain',
  'yandexmarket',
  'yandexmedia',
  'yandexmobilescreenshotbot',
  'yandexnews',
  'yandexontodb',
  'yandexpagechecker',
  'yandexpartner',
  'yandexrca',
  'yandexsearchshop',
  'yandexsitelinks',
  'yandexspravbot',
  'yandextracker',
  'yandexvertis',
  'yandexverticals',
  'yandexwebmaster',
  'yandexscreenshotbot',
  'baiduspider',
  'mail.ru_bot',
  'duckduckbot',
  'twitterbot',
  'facebot',
  'applebot',
  'naver.me/spd',
  'screaming frog seo spider',
] as const;

/**
 * Log all client-side warns in the same way
 *
 * @param {{component: string, message: string}} fields the additional args to describe the error
 * @returns {void}
 */
export const logWarnWrapper = (fields: LogWarnFields): void =>
  logger.logWarn(fields);

export const isKnownBots = (
  userAgent: string | null | undefined,
): (typeof KNOWN_BOT_AGENTS)[number] | undefined | false => {
  if (!userAgent) {
    return false;
  }

  return KNOWN_BOT_AGENTS.find((botAgent) => userAgent.indexOf(botAgent) >= 0);
};

/**
 * Log all client-side errors in the same way
 *
 * @param {Error} error object
 * @param {{component: string, message: string}} fields the additional args to describe the error
 * @returns {void}
 */
export const logErrorWrapper = (
  error: Error,
  { component, message, ...rest }: LogErrorFields,
): void => {
  const userAgent = getUserAgent();
  // Switch level to warn if we recognise the userAgent as a bot
  // https://developers.google.com/search/docs/advanced/crawling/overview-google-crawlers
  if (isKnownBots(userAgent)) {
    const warnFields: Record<string, string | number> = {};
    Object.entries(rest).forEach(([key, value]) => {
      if (typeof value === 'string' || typeof value === 'number') {
        warnFields[key] = value;
      }
    });
    return logWarnWrapper({
      message: `${message}: ${error.message}`,
      component,
      userAgent: userAgent ?? '',
      isKnownBotAgent: 'true',
      ...warnFields,
    });
  }

  return logger.logError(error, {
    component,
    message,
    userAgent,
    ...rest,
  });
};
